import { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { AuthLoading } from '@/components/auth/AuthGuard';
import AuthShell from '@/components/auth/AuthShell';

export default function SignupPage() {
  const { session, profile, loading, profileLoading, signUp } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [needsConfirm, setNeedsConfirm] = useState(false);

  if (loading || (session && profileLoading)) return <AuthLoading />;
  if (session) return <Navigate to={profile?.status === 'approved' ? '/' : '/pending'} replace />;

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    const trimmed = email.trim();
    if (!trimmed || !password) {
      setError('이메일과 비밀번호를 모두 입력해 주세요.');
      return;
    }
    if (password.length < 6) {
      setError('비밀번호는 6자 이상이어야 해요.');
      return;
    }
    if (password !== confirm) {
      setError('비밀번호가 서로 일치하지 않아요.');
      return;
    }
    setSubmitting(true);
    const { error: err, needsConfirm: need } = await signUp(trimmed, password);
    setSubmitting(false);
    if (err) {
      setError(err);
      return;
    }
    if (need) setNeedsConfirm(true);
  };

  if (needsConfirm) {
    return (
      <AuthShell
        title="가입 신청 완료"
        subtitle="이메일 인증이 필요한 상태예요. 메일함에서 인증 링크를 눌러 주세요."
        footer={
          <Link to="/login" className="font-bold text-primary-700 hover:underline">
            로그인 화면으로
          </Link>
        }
      >
        <div className="flex items-start gap-2.5 rounded-md border border-secondary-200 bg-secondary-50 px-3.5 py-3 text-xs leading-relaxed text-foreground-700">
          <i className="ri-mail-check-line mt-0.5 text-base text-secondary-700"></i>
          <span>
            {email.trim()} 으로 인증 메일을 보냈어요. 인증을 마친 뒤 로그인하면, 운영자 승인 대기 상태로 안내됩니다.
          </span>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="회원가입 신청"
      subtitle="가입을 신청하면 운영자가 확인 후 승인해요. 승인되면 바로 오답노트를 사용할 수 있어요."
      footer={
        <>
          이미 계정이 있나요?{' '}
          <Link to="/login" className="font-bold text-primary-700 hover:underline">
            로그인
          </Link>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="flex flex-col gap-4" noValidate>
        <div>
          <label htmlFor="signup-email" className="mb-1.5 block text-xs font-semibold text-foreground-600">
            이메일
          </label>
          <div className="flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3 py-2.5 transition focus-within:border-primary-400 focus-within:ring-2 focus-within:ring-primary-100">
            <i className="ri-mail-line text-base text-foreground-400"></i>
            <input
              id="signup-email"
              type="email"
              name="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="w-full bg-transparent text-sm text-foreground-900 outline-none"
            />
          </div>
        </div>

        <div>
          <label htmlFor="signup-password" className="mb-1.5 block text-xs font-semibold text-foreground-600">
            비밀번호 (6자 이상)
          </label>
          <div className="flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3 py-2.5 transition focus-within:border-primary-400 focus-within:ring-2 focus-within:ring-primary-100">
            <i className="ri-lock-line text-base text-foreground-400"></i>
            <input
              id="signup-password"
              type={showPassword ? 'text' : 'password'}
              name="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="비밀번호"
              className="w-full bg-transparent text-sm text-foreground-900 outline-none"
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="flex h-7 w-7 shrink-0 cursor-pointer items-center justify-center rounded text-foreground-400 transition hover:bg-background-200 hover:text-foreground-700"
              aria-label={showPassword ? '비밀번호 숨기기' : '비밀번호 보기'}
            >
              <i className={showPassword ? 'ri-eye-off-line text-base' : 'ri-eye-line text-base'}></i>
            </button>
          </div>
        </div>

        <div>
          <label htmlFor="signup-confirm" className="mb-1.5 block text-xs font-semibold text-foreground-600">
            비밀번호 확인
          </label>
          <div className="flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3 py-2.5 transition focus-within:border-primary-400 focus-within:ring-2 focus-within:ring-primary-100">
            <i className="ri-lock-2-line text-base text-foreground-400"></i>
            <input
              id="signup-confirm"
              type={showPassword ? 'text' : 'password'}
              name="confirm"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="비밀번호 다시 입력"
              className="w-full bg-transparent text-sm text-foreground-900 outline-none"
            />
          </div>
        </div>

        {error && (
          <div className="flex items-start gap-2 rounded-md border border-accent-200 bg-accent-50 px-3 py-2.5 text-xs leading-relaxed text-accent-800">
            <i className="ri-error-warning-line mt-0.5 text-sm"></i>
            <span>{error}</span>
          </div>
        )}

        <div className="flex items-start gap-2 rounded-md border border-secondary-200 bg-secondary-50 px-3 py-2.5 text-xs leading-relaxed text-foreground-600">
          <i className="ri-information-line mt-0.5 text-sm text-secondary-700"></i>
          <span>가입 후 운영자 승인 전까지는 오답노트를 사용할 수 없어요.</span>
        </div>

        <button
          type="submit"
          disabled={submitting}
          className="mt-1 flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-3 text-sm font-bold text-background-50 transition hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {submitting ? (
            <>
              <i className="ri-loader-4-line animate-spin text-base"></i>
              신청 중...
            </>
          ) : (
            <>
              <i className="ri-user-add-line text-base"></i>
              가입 신청하기
            </>
          )}
        </button>
      </form>
    </AuthShell>
  );
}